import create from 'zustand'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

/** Quaternion [x, y, z, w] for model rotation (first→second = X, second→third = Y). */
export type AlignmentQuaternion = [number, number, number, number]

interface IfcStore {
  /** Origin of the alignment frame picked in the scene. */
  firstPoint: ImmutableVector3 | null
  /** Second picked point — defines the first (X) axis together with firstPoint. */
  secondPoint: ImmutableVector3 | null
  /** Third picked point — defines the perpendicular (Y) axis. */
  thirdPoint: ImmutableVector3 | null
  setFirstPoint: (point: ImmutableVector3) => void
  setSecondPoint: (point: ImmutableVector3) => void
  setThirdPoint: (point: ImmutableVector3) => void
  resetPoints: () => void
  /**
   * Registrations, keyed by caller id, requesting that IFC GLB selection/hover be disabled.
   * The model is treated as non-selectable while any registration is `true`. Managed via the
   * `useDisableIFCSelection` hook so multiple call sites can contribute independently.
   */
  ifcSelectionDisabledById: Map<string, boolean>
  setIfcSelectionDisabled: (id: string, disabled: boolean) => void
  clearIfcSelectionDisabled: (id: string) => void
  /**
   * Per-guid color override for the IFC GLB model. When set, these colors win over the default
   * type-based coloring (e.g. to color by storey). Managed via the `useIFCColoring` hook.
   */
  ifcColorOverrideByGuid: Record<string, string>
  /**
   * Fallback color applied to guids not in `ifcColorOverrideByGuid` while an override is active
   * (e.g. grey for elements without a storey assignment). Null keeps the default type coloring.
   */
  ifcColorOverrideDefault: string | null
  setIfcColorOverride: (guidToColor: Record<string, string>, defaultColor: string | null) => void
  clearIfcColorOverride: () => void
}

/**
 * Points picked in the scene to define an IFC alignment frame (origin + two axes). Consumed by the
 * IFC Import alignment scene tool.
 */
const useIfcStore = create<IfcStore>(set => ({
  firstPoint: null,
  secondPoint: null,
  thirdPoint: null,
  setFirstPoint: firstPoint => set({ firstPoint }),
  setSecondPoint: secondPoint => set({ secondPoint }),
  setThirdPoint: thirdPoint => set({ thirdPoint }),
  resetPoints: () => set({ firstPoint: null, secondPoint: null, thirdPoint: null }),
  ifcSelectionDisabledById: new Map<string, boolean>(),
  setIfcSelectionDisabled: (id, disabled) =>
    set(state => {
      const next = new Map(state.ifcSelectionDisabledById)
      next.set(id, disabled)
      return { ifcSelectionDisabledById: next }
    }),
  clearIfcSelectionDisabled: id =>
    set(state => {
      const next = new Map(state.ifcSelectionDisabledById)
      next.delete(id)
      return { ifcSelectionDisabledById: next }
    }),
  ifcColorOverrideByGuid: {},
  ifcColorOverrideDefault: null,
  setIfcColorOverride: (guidToColor, defaultColor) =>
    set({ ifcColorOverrideByGuid: guidToColor, ifcColorOverrideDefault: defaultColor }),
  clearIfcColorOverride: () => set({ ifcColorOverrideByGuid: {}, ifcColorOverrideDefault: null }),
}))

export default useIfcStore
